import React, { useState } from "react";
import { Clock, Users, AlertTriangle, MapPin, GripVertical, CheckCircle2, PlayCircle, CalendarClock } from "lucide-react";
import { STATUS_COLS } from "./kanbanUtils";
import { log } from "../../../lib/log";
import { joinAddress } from "../../../lib/address";

const ST_ICON = { planifiee: CalendarClock, en_cours: PlayCircle, terminee: CheckCircle2 };
const ST_CLS = { planifiee: "bg-neutral-200 text-neutral-700", en_cours: "bg-amber-100 text-amber-700", terminee: "bg-emerald-100 text-emerald-700" };
const hm = (x) => (x ? new Date(x).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" }) : "—");
const dayShort = (x) => (x ? new Date(x).toLocaleDateString("fr-FR", { weekday: "short", day: "numeric", month: "short" }) : "");

export const KanbanCard = ({ it, mode, colKey, onOpen, onDragStart }) => {
  const draggable = mode !== "day" || it.status === "planifiee";
  const Icon = ST_ICON[it.status] || CalendarClock;
  const label = STATUS_COLS.find((c) => c.key === it.status)?.label || "Planifiée";
  return (
    <div draggable={draggable} onDragStart={(e) => onDragStart(e, it, colKey)} onClick={() => onOpen(it)}
      title={draggable ? "Glisser pour déplacer · cliquer pour modifier" : "Seule une intervention planifiée peut être replanifiée"}
      className={`bg-white border rounded-lg p-3 shadow-sm hover:shadow-md transition-shadow cursor-pointer select-none ${it.needs_reassign ? "border-red-300" : "border-neutral-200"} ${draggable ? "" : "opacity-70"}`}
      data-testid={`kanban-card-${it.id}`}>
      <div className="flex items-start gap-1.5">
        <GripVertical size={14} className="text-neutral-300 mt-0.5 shrink-0" />
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-sm text-neutral-900 truncate">{it.client_nom}</p>
          <p className="text-xs text-neutral-500 truncate">{it.prestation || "Nettoyage"}</p>
          <p className="text-xs text-neutral-600 mt-1 flex items-center gap-1"><Clock size={11} /> {mode === "day" ? hm(it.date) : `${dayShort(it.date)} · ${hm(it.date)}`}</p>
          {(it.adresse || it.ville) && <p className="text-[11px] text-neutral-400 truncate flex items-center gap-1"><MapPin size={10} /> {joinAddress(it.adresse, it.ville)}</p>}
          <div className="flex flex-wrap gap-1 mt-1.5">
            {mode !== "status" && <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full flex items-center gap-0.5 ${ST_CLS[it.status] || ST_CLS.planifiee}`}><Icon size={10} /> {label}</span>}
            {mode !== "easer" && (it.collaborateurs || []).map((c) => <span key={c.login || c.nom} className="text-[10px] bg-neutral-100 text-neutral-600 px-1.5 py-0.5 rounded-full flex items-center gap-0.5"><Users size={10} /> {c.nom}</span>)}
            {it.needs_reassign && <span className="text-[10px] font-semibold bg-red-600 text-white px-1.5 py-0.5 rounded-full flex items-center gap-0.5"><AlertTriangle size={10} /> À réaffecter</span>}
            {it.gps_alert && <span className="text-[10px] font-semibold bg-red-100 text-red-700 px-1.5 py-0.5 rounded-full">Hors zone</span>}
          </div>
        </div>
      </div>
    </div>
  );
};

export const KanbanColumn = ({ col, mode, onOpen, onDrop }) => {
  const [over, setOver] = useState(false);
  const onDragStart = (e, it, fromKey) => {
    e.dataTransfer.setData("text/plain", JSON.stringify({ id: it.id, from: fromKey }));
    e.dataTransfer.effectAllowed = "move";
  };
  const drop = (e) => {
    e.preventDefault(); setOver(false);
    try { const { id, from } = JSON.parse(e.dataTransfer.getData("text/plain")); if (from !== col.key) onDrop(id, from, col.key); } catch (err) { log.error(err); }
  };
  return (
    <div onDragOver={(e) => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)} onDrop={drop}
      className={`flex-shrink-0 w-64 bg-neutral-50 rounded-xl border border-t-4 ${col.cls} ${over ? "ring-2 ring-brand bg-brand/5" : "border-neutral-200"} flex flex-col max-h-[70vh]`}
      data-testid={`kanban-col-${col.key}`}>
      <div className="px-3 py-2.5 flex items-center justify-between">
        <div><p className="text-sm font-bold text-neutral-800">{col.label}</p>{col.sub && <p className="text-[11px] text-neutral-500">{col.sub}</p>}</div>
        <span className="text-xs font-semibold bg-white border border-neutral-200 rounded-full px-2 py-0.5 text-neutral-600" data-testid={`kanban-count-${col.key}`}>{col.items.length}</span>
      </div>
      <div className="px-2 pb-2 space-y-2 overflow-y-auto flex-1">
        {col.items.map((it) => <KanbanCard key={it.id} it={it} mode={mode} colKey={col.key} onOpen={onOpen} onDragStart={onDragStart} />)}
        {col.items.length === 0 && <p className="text-xs text-neutral-400 text-center py-6">Déposer ici</p>}
      </div>
    </div>
  );
};
